import { ANYTIME_PREFERENCE_VALUE } from 'constants/advancedSearch';
import isObject from 'lodash/isObject';
import moment from 'moment';
import { DateFormats } from 'UI/constants/defaults';

export const stripNonDigits = val => val.replace(/\D/g, '') || null;

export const formatNameOptionSubtitle = (metadata = {}) => {
  const position = metadata.position || '';
  const location = metadata.location || '';
  return `${position} / ${location}`;
};

export const keepFirstSelectionByName = selectedItems => {
  const seenNames = new Set();
  return selectedItems.filter(selection => {
    const name = selection?.item?.value;
    if (seenNames.has(name)) return false;
    seenNames.add(name);
    return true;
  });
};

const toDateRangeBounds = (startDate, endDate) => {
  const from = `${moment(startDate, DateFormats.QueryShortFormat).format(
    DateFormats.QueryShortFormat
  )}T00:00:00Z`;
  const to = `${moment(endDate, DateFormats.QueryShortFormat).format(
    DateFormats.QueryShortFormat
  )}T23:59:59Z`;
  return { from, to };
};

const COORDINATES_PRIORITY = 'must_have';

const flattenFilterEntries = filterData => {
  const {
    fieldName,
    preferencesFieldName,
    salaryTypeFieldName,
    coordinatesFieldName,
    isDateRange,
    value
  } = filterData;

  if (fieldName && isDateRange && isObject(value) && value !== null) {
    const { startDate, endDate } = value;
    if (!startDate || !endDate) return [];

    const { from, to } = toDateRangeBounds(startDate, endDate);
    return [
      {
        item: { value: { between: [from, to] } },
        priority: 'must_have',
        fieldName,
        preferencesFieldName: null
      }
    ];
  }

  if (fieldName && Array.isArray(value)) {
    return value.map(item => {
      const withMeta = { ...item, fieldName, preferencesFieldName };
      if (!preferencesFieldName || item.preference !== undefined) return withMeta;
      return { ...withMeta, preference: ANYTIME_PREFERENCE_VALUE };
    });
  }

  if (fieldName && coordinatesFieldName && isObject(value) && value !== null) {
    const { cities, radius, defaultPriority = 'must_have' } = value;
    if (!cities || cities.length === 0) return [];

    const entries = cities.map(({ city, priority: cityPriority }) => ({
      item: { value: city },
      priority: cityPriority || defaultPriority,
      fieldName,
      preferencesFieldName: null
    }));

    if (radius !== null && radius !== undefined && radius !== '') {
      entries.push({
        item: { value: { radius: Number(radius) } },
        priority: COORDINATES_PRIORITY,
        fieldName: coordinatesFieldName,
        preferencesFieldName: null,
        isSingleValue: true
      });
    }

    return entries;
  }

  if (fieldName && salaryTypeFieldName && isObject(value) && value !== null) {
    const { type, min, max } = value;
    const hasType = type !== null && type !== undefined && String(type).trim() !== '';
    const hasRange =
      (min !== null && min !== undefined && min !== '') ||
      (max !== null && max !== undefined && max !== '');
    if (!hasType || !hasRange) return [];

    const rangeValue = {};
    const parsedMin = min !== null && min !== undefined && min !== '' ? Number(min) : undefined;
    const parsedMax = max !== null && max !== undefined && max !== '' ? Number(max) : undefined;
    if (parsedMin !== undefined && Number.isFinite(parsedMin)) rangeValue.gte = parsedMin;
    if (parsedMax !== undefined && Number.isFinite(parsedMax)) rangeValue.lte = parsedMax;

    if (
      rangeValue.gte !== undefined &&
      rangeValue.lte !== undefined &&
      rangeValue.gte > rangeValue.lte
    )
      return [];

    const salaryPriority = value.priority || 'must_have';
    return [
      {
        item: { value: rangeValue },
        priority: salaryPriority,
        fieldName,
        preferencesFieldName: null
      },
      {
        item: { value: type },
        priority: salaryPriority,
        fieldName: salaryTypeFieldName,
        preferencesFieldName: null,
        isSingleValue: true
      }
    ];
  }

  if (fieldName && isObject(value)) {
    return [
      {
        ...value,
        fieldName,
        preferencesFieldName
      }
    ];
  }

  return [];
};

const createFilterKey = (fieldName, priority) => `${fieldName}___${priority}`;
// Each activity item must be sent as its own filter group — a per-item discriminator
// (the item's position in the flattened list) keeps items from merging even when
// they share the same field, priority and preference.
const createPreferenceFilterKey = ({ fieldName, preference, priority, discriminator }) =>
  `${createFilterKey(fieldName, priority)}___${preference}___${discriminator}`;

const buildPreferenceFilter = ({ fieldName, preferencesFieldName, preference, priority }) => ({
  priority,
  filters: [
    {
      field: fieldName,
      value: []
    },
    {
      field: preferencesFieldName,
      value: preference
    }
  ]
});

const normalizeTimePreferenceValue = preference => ({
  last: preference === ANYTIME_PREFERENCE_VALUE ? null : preference
});

export const createFiltersRequestPayload = (formValues, priorityMap) => {
  if (!formValues || typeof formValues !== 'object') {
    return {
      filters: [],
      size: 10
    };
  }

  const filters = Object.values(formValues)
    .flatMap(flattenFilterEntries)
    .reduce((acc, item, index) => {
      const priority = priorityMap[item.priority] || priorityMap.must_have;
      const isPreferenceFieldSet = Boolean(
        item.preferencesFieldName && item.preference !== undefined
      );

      if (isPreferenceFieldSet) {
        const preferenceKey = createPreferenceFilterKey({
          fieldName: item.fieldName,
          preference: item.preference,
          priority,
          discriminator: index
        });

        if (!acc[preferenceKey]) {
          acc[preferenceKey] = buildPreferenceFilter({
            fieldName: item.fieldName,
            preferencesFieldName: item.preferencesFieldName,
            preference: normalizeTimePreferenceValue(item.preference),
            priority
          });
        }

        if (typeof item.item.value === 'string' || typeof item.item.value === 'number') {
          acc[preferenceKey].filters[0].value.push(item.item.value);
          return acc;
        }

        acc[preferenceKey].filters[0].value = item.item.value;
        return acc;
      }

      const key = createFilterKey(item.fieldName, priority);

      if (!acc[key]) {
        acc[key] = {
          field: item.fieldName,
          priority,
          value: item.isSingleValue ? null : []
        };
      }

      if (item.isSingleValue) {
        acc[key].value = item.item.value;
        return acc;
      }

      if (typeof item.item.value === 'string' || typeof item.item.value === 'number') {
        acc[key].value.push(item.item.value);
        return acc;
      }

      acc[key].value = item.item.value;
      return acc;
    }, {});

  return {
    filters: Object.values(filters),
    size: 10
  };
};
